import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../config';
import SharedNav from './SharedNav';
import './ProposalsPage.css';

const DIFFICULTIES = ['Very Difficult', 'Difficult', 'Difficult/Moderate', 'Moderate', 'Easy', 'Unknown'];

function emptyStation() {
  return { station_number: '', city: '', state: '', difficulty: 'Moderate', csa_towns: '', notes: '', search_radius_miles: 75, _found: false };
}

function emptyForm() {
  return {
    company_name: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    stations: [emptyStation()],
    pricing_tier: 'single',
    pricing_custom: '',
    context_urgency: 'Moderate — planning ahead',
    context_pain_points: '',
    context_notes: '',
    context_raw_message: '',
  };
}

function formatDate(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function difficultyClass(d) {
  if (d === 'Very Difficult') return '#991b1b';
  if (d === 'Difficult') return '#ea580c';
  if (d === 'Difficult/Moderate') return '#b45309';
  if (d === 'Moderate') return '#2563eb';
  if (d === 'Easy') return '#16a34a';
  return '#6b7280';
}

export default function ProposalsPage({ user, onLogout }) {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const authHeaders = { Authorization: `Bearer ${token}` };
  const isAdmin = user?.role?.toLowerCase() === 'admin';

  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [generating, setGenerating] = useState(false);
  const [progressMsg, setProgressMsg] = useState('');
  const [successUrl, setSuccessUrl] = useState(null);
  const [copiedSlug, setCopiedSlug] = useState(null);
  const [copiedSuccess, setCopiedSuccess] = useState(false);
  const [regenId, setRegenId] = useState(null);
  const [editId, setEditId] = useState(null);
  const [draftSaved, setDraftSaved] = useState(false);
  const [debugResult, setDebugResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/proposals`, { headers: authHeaders });
      setProposals(res.data);
    } catch (e) {
      console.error('Failed to load proposals', e);
    }
    setLoading(false);
  }, []); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  // Auto-save draft every 15 seconds when form is open (new proposals only, not edits)
  useEffect(() => {
    if (!showForm || editId) return;
    const interval = setInterval(() => {
      localStorage.setItem('proposals_draft', JSON.stringify(form));
      setDraftSaved(true);
      setTimeout(() => setDraftSaved(false), 2500);
    }, 15000);
    return () => clearInterval(interval);
  }, [showForm, form, editId]);

  // ── Station helpers ──
  const updateStation = (i, field, value) => {
    setForm(f => {
      const stations = [...f.stations];
      stations[i] = { ...stations[i], [field]: value };
      return { ...f, stations };
    });
  };

  const addStation = () => {
    if (form.stations.length >= 5) return;
    setForm(f => ({ ...f, stations: [...f.stations, emptyStation()] }));
  };

  const removeStation = (i) => {
    setForm(f => ({ ...f, stations: f.stations.filter((_, idx) => idx !== i) }));
  };

  const lookupStation = async (i) => {
    const num = form.stations[i].station_number;
    if (!num) return;
    try {
      const res = await axios.get(`${API_URL}/api/proposals/station-lookup?number=${num}`, { headers: authHeaders });
      if (res.data.found) {
        const s = res.data.station;
        setForm(f => {
          const stations = [...f.stations];
          stations[i] = { ...stations[i], city: s.city || '', state: s.state || '', difficulty: s.difficulty || 'Moderate', _found: true, _foundLabel: `${s.city}, ${s.state} — ${s.difficulty}` };
          return { ...f, stations };
        });
      }
    } catch (e) { /* ignore */ }
  };

  // ── Pricing suggestion ──
  const suggestedTier = form.stations.length > 1 ? 'bundle' : 'single';
  const pricingAmount = form.pricing_custom
    ? parseInt(form.pricing_custom)
    : (form.pricing_tier === 'bundle' ? 1299 : 899);

  // ── Generate proposal ──
  const handleGenerate = async () => {
    const { company_name, contact_name, stations } = form;
    if (!company_name.trim() || !contact_name.trim()) {
      alert('Company name and contact name are required.');
      return;
    }
    if (stations.some(s => !s.city.trim() || !s.state.trim())) {
      alert('Please fill in City and State for all stations.');
      return;
    }

    setGenerating(true);
    setSuccessUrl(null);
    setProgressMsg('Scanning competitor job ads...');

    const payload = {
      company: { name: company_name, contact_name, contact_email: form.contact_email, contact_phone: form.contact_phone },
      stations: stations.map(s => ({
        station_number: s.station_number,
        city: s.city,
        state: s.state,
        difficulty: s.difficulty,
        csa_towns: s.csa_towns ? s.csa_towns.split(',').map(t => t.trim()).filter(Boolean) : [],
        notes: s.notes,
        search_radius_miles: parseInt(s.search_radius_miles) || 75,
      })),
      pricing: { tier: form.pricing_tier, amount: pricingAmount },
      context: {
        urgency: form.context_urgency,
        pain_points: form.context_pain_points,
        notes: form.context_notes,
        raw_client_message: form.context_raw_message || null,
      },
    };

    try {
      const timer = setInterval(() => {
        setProgressMsg(prev => {
          const steps = ['Scanning competitor job ads...', 'Classifying results with AI...', 'Writing proposal content...', 'Generating HTML...', 'Almost done...'];
          const idx = steps.indexOf(prev);
          return steps[Math.min(idx + 1, steps.length - 1)];
        });
      }, 12000);

      const res = editId
        ? await axios.put(`${API_URL}/api/proposals/${editId}`, payload, { headers: authHeaders, timeout: 240000 })
        : await axios.post(`${API_URL}/api/proposals/generate`, payload, { headers: authHeaders, timeout: 240000 });
      clearInterval(timer);

      setSuccessUrl(res.data.url);
      localStorage.removeItem('proposals_draft');
      if (!editId) setForm(emptyForm());
      await load();
    } catch (e) {
      alert('Generation failed: ' + (e.response?.data?.error || e.message));
    }
    setGenerating(false);
    setProgressMsg('');
  };

  const copyLink = (url, id) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopiedSlug(id);
      setTimeout(() => setCopiedSlug(null), 2000);
    });
  };

  const deleteProposal = async (id, name) => {
    if (!window.confirm(`Delete proposal for ${name}?`)) return;
    try {
      await axios.delete(`${API_URL}/api/proposals/${id}`, { headers: authHeaders });
      setProposals(p => p.filter(x => x.id !== id));
    } catch (e) {
      alert('Delete failed: ' + (e.response?.data?.error || e.message));
    }
  };

  const debugScan = async (city, state) => {
    try {
      const res = await axios.get(`${API_URL}/api/proposals/debug-scan`, {
        headers: authHeaders,
        params: { city, state },
        timeout: 60000,
      });
      setDebugResult(JSON.stringify(res.data, null, 2));
    } catch (e) {
      setDebugResult('Error: ' + (e.response?.data?.error || e.message));
    }
  };

  const regenerateProposal = async (id, name) => {
    setRegenId(id);
    try {
      await axios.post(`${API_URL}/api/proposals/${id}/regenerate`, {}, {
        headers: authHeaders,
        timeout: 300000,
      });
    } catch (e) {
      alert('Regenerate failed: ' + (e.response?.data?.error || e.message));
    }
    setRegenId(null);
  };

  const openForm = () => {
    setEditId(null);
    const saved = localStorage.getItem('proposals_draft');
    if (saved) {
      try { setForm(JSON.parse(saved)); } catch { setForm(emptyForm()); }
    } else {
      setForm(emptyForm());
    }
    setSuccessUrl(null);
    setShowForm(true);
  };

  const openEditForm = (p) => {
    const inp = typeof p.inputs === 'string' ? JSON.parse(p.inputs) : (p.inputs || {});
    const stdAmount = (inp.pricing?.tier === 'bundle') ? 1299 : 899;
    const isCustomPrice = inp.pricing?.amount && inp.pricing.amount !== stdAmount;
    setForm({
      company_name: inp.company?.name || p.company_name || '',
      contact_name: inp.company?.contact_name || p.contact_name || '',
      contact_email: inp.company?.contact_email || p.contact_email || '',
      contact_phone: inp.company?.contact_phone || '',
      stations: (inp.stations || []).map(s => ({
        station_number: s.station_number || '',
        city: s.city || '',
        state: s.state || '',
        difficulty: s.difficulty || 'Moderate',
        csa_towns: Array.isArray(s.csa_towns) ? s.csa_towns.join(', ') : (s.csa_towns || ''),
        notes: s.notes || '',
        search_radius_miles: s.search_radius_miles || 75,
        _found: false,
      })),
      pricing_tier: inp.pricing?.tier || 'single',
      pricing_custom: isCustomPrice ? String(inp.pricing.amount) : '',
      context_urgency: inp.context?.urgency || 'Moderate — planning ahead',
      context_pain_points: inp.context?.pain_points || '',
      context_notes: inp.context?.notes || '',
      context_raw_message: inp.context?.raw_client_message || '',
    });
    setEditId(p.id);
    setSuccessUrl(null);
    setShowForm(true);
  };

  const closeForm = () => {
    if (generating) return;
    setShowForm(false);
    setSuccessUrl(null);
    setEditId(null);
  };

  return (
    <div className="proposals-page">
      {/* Header */}
      <SharedNav user={user} onLogout={onLogout} />

      {/* Toolbar */}
      <div className="proposals-toolbar">
        <button className="btn-generate" onClick={openForm} style={{ marginLeft: 'auto' }}>
          + Generate Proposal
        </button>
      </div>

      {/* Success banner (persists outside modal after generation) */}
      {successUrl && !showForm && (
        <div className="proposal-success-banner">
          <span className="success-icon">✅</span>
          <div className="success-text">
            <strong>Proposal generated successfully!</strong>
            <div className="success-url-row">
              <a href={successUrl} target="_blank" rel="noreferrer" className="success-url">{successUrl}</a>
              <button
                className={`btn-copy-success${copiedSuccess ? ' copied' : ''}`}
                onClick={() => { navigator.clipboard.writeText(successUrl); setCopiedSuccess(true); setTimeout(() => setCopiedSuccess(false), 2000); }}
              >
                {copiedSuccess ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Proposals table */}
      <div className="proposals-table-wrap mtn-scroll-x">
        {loading ? (
          <div className="empty-proposals"><p>Loading...</p></div>
        ) : proposals.length === 0 ? (
          <div className="empty-proposals">
            <h3>No proposals yet</h3>
            <p>Click "Generate Proposal" to create your first one.</p>
          </div>
        ) : (
          <table className="proposals-table">
            <thead>
              <tr>
                <th>Company</th>
                <th>Stations</th>
                <th>Market Ads</th>
                <th>Pricing</th>
                <th>Created</th>
                <th>Views</th>
                <th>Link</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {proposals.map(p => {
                const inputs = typeof p.inputs === 'string' ? JSON.parse(p.inputs) : (p.inputs || {});
                const market = typeof p.market_data === 'string' ? JSON.parse(p.market_data) : (p.market_data || []);
                const totalAds = Array.isArray(market)
                  ? market.reduce((sum, m) => sum + (m?.fedex_pd_count ?? 0), 0)
                  : null;
                const pricing = inputs.pricing;
                const proposalUrl = `${window.location.origin}/p/${p.slug}`;
                const isExternal = p.source === 'external';
                return (
                  <tr key={p.id} style={isExternal ? { background: 'rgba(220,38,38,0.07)', outline: '1px solid rgba(220,38,38,0.18)' } : {}}>
                    <td>
                      <div className="proposal-company-name" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {p.company_name}
                        {isExternal && <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', background: '#fee2e2', color: '#991b1b', padding: '2px 7px', borderRadius: 4 }}>Web Lead</span>}
                      </div>
                      {p.contact_name && <div className="proposal-contact">{p.contact_name}{p.contact_email ? ` · ${p.contact_email}` : ''}</div>}
                      {!p.contact_name && p.contact_email && <div className="proposal-contact">{p.contact_email}</div>}
                    </td>
                    <td>
                      {inputs.stations?.map((s, i) => (
                        <span key={i} className="station-badge" style={{ borderLeft: `3px solid ${difficultyClass(s.difficulty)}` }}>
                          {s.station_number ? `#${s.station_number}` : s.city} {s.state}
                        </span>
                      ))}
                    </td>
                    <td>
                      {totalAds === null ? <span style={{ color: '#94a3b8' }}>—</span> : (
                        <span style={{ fontWeight: 700, color: totalAds === 0 ? '#16a34a' : totalAds <= 3 ? '#d97706' : '#dc2626' }}>
                          {totalAds} ads
                        </span>
                      )}
                    </td>
                    <td>
                      {pricing ? `$${pricing.amount}/mo` : '—'}
                    </td>
                    <td>{formatDate(p.created_at)}</td>
                    <td>
                      <span className="view-count-badge">
                        👁 {p.view_count || 0}
                        {p.last_viewed_at && <span style={{ fontSize: 11, color: '#94a3b8' }}> · {formatDate(p.last_viewed_at)}</span>}
                      </span>
                    </td>
                    <td className="proposal-url-cell">
                      <a href={proposalUrl} target="_blank" rel="noreferrer" className="proposal-url-link">{proposalUrl}</a>
                      <button
                        className={`copy-link-btn${copiedSlug === p.id ? ' copied' : ''}`}
                        onClick={() => copyLink(proposalUrl, p.id)}
                      >
                        {copiedSlug === p.id ? '✓ Copied' : 'Copy'}
                      </button>
                    </td>
                    <td style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                      <button
                        className="mtn-btn mtn-btn-edit"
                        onClick={() => openEditForm(p)}
                        title="Edit inputs and regenerate"
                      >
                        <span className="mtn-ic">✏</span>Edit
                      </button>
                      <button
                        className={`mtn-btn mtn-btn-neutral${regenId === p.id ? ' mtn-btn-disabled' : ''}`}
                        onClick={() => regenerateProposal(p.id, p.company_name)}
                        disabled={regenId === p.id}
                        title="Re-generate HTML with latest template (same inputs)"
                      >
                        {regenId === p.id ? '⟳ Updating...' : '⟳ Regen'}
                      </button>
                      {isAdmin && (
                        <button
                          className="mtn-btn mtn-btn-neutral"
                          onClick={() => debugScan(p.stations?.[0]?.city || '', p.stations?.[0]?.state || '')}
                          title="Test raw SerpAPI results for this station"
                        >
                          🔍 Debug
                        </button>
                      )}
                      {isAdmin && (
                        <button className="mtn-btn mtn-btn-delete" onClick={() => deleteProposal(p.id, p.company_name)}>Delete</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Generate modal */}
      {showForm && (
        <div className="generate-modal-overlay" onClick={e => { if (e.target === e.currentTarget) closeForm(); }}>
          <div className="generate-modal" style={{ position: 'relative' }}>

            {/* Generating overlay */}
            {generating && (
              <div className="generating-overlay">
                <div className="generating-spinner" />
                <div className="generating-label">{progressMsg || 'Generating proposal...'}</div>
                <div className="generating-sublabel">This takes 30–90 seconds. Please wait.</div>
              </div>
            )}

            <div className="generate-modal-header">
              <div>
                <h3>{editId ? `Edit Proposal — ${form.company_name || ''}` : 'Generate New Proposal'}</h3>
                <p>{editId ? 'Update inputs and re-run market scout + Claude to regenerate the proposal.' : 'Runs market-scout automatically per station, then builds proposal with Claude.'}</p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {!editId && draftSaved && (
                  <span style={{ fontSize: 12, color: '#16a34a', fontWeight: 500 }}>✓ Draft saved</span>
                )}
                {!editId && !draftSaved && localStorage.getItem('proposals_draft') && (
                  <span style={{ fontSize: 12, color: '#6b7280' }}>Draft restored</span>
                )}
                <button className="modal-close-btn" onClick={closeForm}>×</button>
              </div>
            </div>

            {/* Success inside modal */}
            {successUrl && (
              <div className="proposal-success-banner" style={{ margin: '20px 28px 0' }}>
                <span className="success-icon">✅</span>
                <div className="success-text">
                  <strong>Proposal ready!</strong>
                  <div className="success-url-row">
                    <a href={successUrl} target="_blank" rel="noreferrer" className="success-url">{successUrl}</a>
                    <button
                      className={`btn-copy-success${copiedSuccess ? ' copied' : ''}`}
                      onClick={() => { navigator.clipboard.writeText(successUrl); setCopiedSuccess(true); setTimeout(() => setCopiedSuccess(false), 2000); }}
                    >
                      {copiedSuccess ? 'Copied!' : 'Copy Link'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="generate-modal-body">

              {/* Company info */}
              <div className="form-section">
                <div className="form-section-title">Client Info</div>
                <div className="form-grid-2">
                  <div className="form-field">
                    <label>Company Name <span className="req">*</span></label>
                    <input value={form.company_name} onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))} placeholder="BE BELL, INC" />
                  </div>
                  <div className="form-field">
                    <label>Contact Name <span className="req">*</span></label>
                    <input value={form.contact_name} onChange={e => setForm(f => ({ ...f, contact_name: e.target.value }))} placeholder="Brandon Bell" />
                  </div>
                  <div className="form-field">
                    <label>Contact Email</label>
                    <input type="email" value={form.contact_email} onChange={e => setForm(f => ({ ...f, contact_email: e.target.value }))} placeholder="brandon@example.com" />
                  </div>
                  <div className="form-field">
                    <label>Contact Phone</label>
                    <input value={form.contact_phone} onChange={e => setForm(f => ({ ...f, contact_phone: e.target.value }))} placeholder="(605) 555-0100" />
                  </div>
                </div>
              </div>

              {/* Stations */}
              <div className="form-section">
                <div className="form-section-title">Stations ({form.stations.length})</div>
                {form.stations.map((s, i) => (
                  <div key={i} className="station-block">
                    <div className="station-block-header">
                      <span className="station-block-label">Station {i + 1}</span>
                      {form.stations.length > 1 && (
                        <button className="btn-remove-station" onClick={() => removeStation(i)}>Remove</button>
                      )}
                    </div>
                    <div className="station-lookup-row" style={{ marginBottom: 14 }}>
                      <div className="form-field">
                        <label>Station #</label>
                        <input
                          value={s.station_number}
                          onChange={e => updateStation(i, 'station_number', e.target.value)}
                          placeholder="575"
                          onKeyDown={e => e.key === 'Enter' && lookupStation(i)}
                        />
                      </div>
                      <button className="btn-lookup" onClick={() => lookupStation(i)}>Auto-Lookup</button>
                    </div>
                    {s._found && <div className="station-found-tag">✓ Found: {s._foundLabel}</div>}
                    <div className="form-grid-2">
                      <div className="form-field">
                        <label>City <span className="req">*</span></label>
                        <input value={s.city} onChange={e => updateStation(i, 'city', e.target.value)} placeholder="Pierre" />
                      </div>
                      <div className="form-field">
                        <label>State <span className="req">*</span></label>
                        <input value={s.state} onChange={e => updateStation(i, 'state', e.target.value)} placeholder="SD" maxLength={2} />
                      </div>
                      <div className="form-field">
                        <label>Difficulty</label>
                        <select value={s.difficulty} onChange={e => updateStation(i, 'difficulty', e.target.value)}>
                          {DIFFICULTIES.map(d => <option key={d}>{d}</option>)}
                        </select>
                      </div>
                      <div className="form-field">
                        <label>Search Radius (miles)</label>
                        <input type="number" value={s.search_radius_miles} onChange={e => updateStation(i, 'search_radius_miles', e.target.value)} />
                      </div>
                      <div className="form-field" style={{ gridColumn: '1 / -1' }}>
                        <label>CSA Towns (comma-separated, optional)</label>
                        <input value={s.csa_towns} onChange={e => updateStation(i, 'csa_towns', e.target.value)} placeholder="Valentine NE, Ainsworth NE, Winner SD" />
                      </div>
                      <div className="form-field" style={{ gridColumn: '1 / -1' }}>
                        <label>Notes for this station</label>
                        <input value={s.notes} onChange={e => updateStation(i, 'notes', e.target.value)} placeholder="e.g. high turnover, rural area, ISP expanding routes" />
                      </div>
                    </div>
                  </div>
                ))}
                {form.stations.length < 5 && (
                  <button className="btn-add-station" onClick={addStation}>+ Add Another Station</button>
                )}
              </div>

              {/* Pricing */}
              <div className="form-section">
                <div className="form-section-title">Pricing</div>
                <div className="pricing-toggle">
                  <div
                    className={`pricing-option${form.pricing_tier === 'single' ? ' selected' : ''}`}
                    onClick={() => setForm(f => ({ ...f, pricing_tier: 'single' }))}
                  >
                    <div className="pricing-option-label">Single Station</div>
                    <div className="pricing-option-price">$899</div>
                    <div className="pricing-option-note">/month</div>
                  </div>
                  <div
                    className={`pricing-option${form.pricing_tier === 'bundle' ? ' selected' : ''}`}
                    onClick={() => setForm(f => ({ ...f, pricing_tier: 'bundle' }))}
                  >
                    <div className="pricing-option-label">Bundle (2+ stations)</div>
                    <div className="pricing-option-price">$1,299</div>
                    <div className="pricing-option-note">/month · recommended</div>
                  </div>
                </div>
                <div className="form-field" style={{ marginTop: 12 }}>
                  <label>Custom Price Override (optional)</label>
                  <input type="number" value={form.pricing_custom} onChange={e => setForm(f => ({ ...f, pricing_custom: e.target.value }))} placeholder={`Leave blank for $${form.pricing_tier === 'bundle' ? '1,299' : '899'}`} />
                </div>
                {form.stations.length > 1 && form.pricing_tier === 'single' && (
                  <p style={{ fontSize: 12, color: '#d97706', marginTop: 8 }}>⚠️ For {form.stations.length} stations, Bundle ($1,299) is recommended.</p>
                )}
              </div>

              {/* Context */}
              <div className="form-section">
                <div className="form-section-title">Context</div>
                <div className="form-grid-1">
                  <div className="form-field">
                    <label>Urgency</label>
                    <select value={form.context_urgency} onChange={e => setForm(f => ({ ...f, context_urgency: e.target.value }))}>
                      <option>Urgent — losing drivers now</option>
                      <option>Moderate — planning ahead</option>
                      <option>Exploring options</option>
                    </select>
                  </div>
                  <div className="form-field">
                    <label>Primary Pain Point</label>
                    <input value={form.context_pain_points} onChange={e => setForm(f => ({ ...f, context_pain_points: e.target.value }))} placeholder="e.g. can't find rural drivers, high turnover every 2 months" />
                  </div>
                  <div className="form-field">
                    <label>Additional Notes</label>
                    <input value={form.context_notes} onChange={e => setForm(f => ({ ...f, context_notes: e.target.value }))} placeholder="Anything else for the proposal writer" />
                  </div>
                  <div className="form-field">
                    <label>Paste Client Email / Message (optional — helps personalize)</label>
                    <textarea
                      value={form.context_raw_message}
                      onChange={e => setForm(f => ({ ...f, context_raw_message: e.target.value }))}
                      placeholder="Paste the email or message the client sent you..."
                      rows={4}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="generate-modal-footer">
              <button className="btn-cancel" onClick={closeForm} disabled={generating}>Cancel</button>
              <button className="btn-generate-submit" onClick={handleGenerate} disabled={generating}>
                {generating
                  ? <><span className="generating-spinner" style={{ width: 16, height: 16, borderWidth: 2 }} /> {editId ? 'Updating...' : 'Generating...'}</>
                  : editId ? '✨ Save & Regenerate' : '✨ Generate Proposal'}
              </button>
            </div>
          </div>
        </div>
      )}

      {debugResult && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={() => setDebugResult(null)}>
          <div style={{ background:'#0f172a', color:'#e2e8f0', borderRadius:10, padding:24, width:'90vw', maxWidth:900, maxHeight:'85vh', overflow:'auto', fontFamily:'monospace', fontSize:12 }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
              <strong style={{ color:'#7c3aed', fontSize:14 }}>SerpAPI Debug Output</strong>
              <button onClick={() => setDebugResult(null)} style={{ background:'none', border:'none', color:'#94a3b8', fontSize:18, cursor:'pointer' }}>✕</button>
            </div>
            <pre style={{ margin:0, whiteSpace:'pre-wrap', wordBreak:'break-all' }}>{debugResult}</pre>
          </div>
        </div>
      )}
    </div>
  );
}
